import bowser from 'bowser'
import simkey from 'simkey'
import angular from 'angular'
import inspect from 'object-inspect'
import DOMPurify from 'dompurify'
import screenfull from 'screenfull'
import simulateEvent from 'simulate-event'

import debugRaw from '../utils/debug-raw'

import { getElmStr } from '../utils/get-elm-str'
import { offEvent, onEventPassive } from '../utils/on-event'
import { wrap, noop, once, assign, functions } from 'lodash-es'

import deline from '../utils/deline'
import envRaw from '../utils/env-raw'
import extractRawElm from '../utils/extract-raw-elm'

import { GALLERY_INCLUDE_ATTR } from '../utils/shared-constants'

import bodyResetStyles from './styles.body-reset.raw.less'
import galleryHardRestStyles from './styles.gallery-hard-reset.raw.less'

const styles = {
  bodyReset: { name: 'styles.body-reset.raw.less', text: bodyResetStyles },
  hardReset: { name: 'styles.gallery-hard-reset.raw.less', text: galleryHardRestStyles }
}

debugRaw({
  bowser,
  simkey,
  angular,
  inspect,
  DOMPurify,
  screenfull,
  simulateEvent
})

angular
  .module('ggs')
  .config(($compileProvider) => {
    // https://docs.angularjs.org/api/ng/provider/$compileProvider
    $compileProvider.debugInfoEnabled(envRaw.devMode)
    $compileProvider.strictComponentBindingsEnabled(true)
    // https://github.com/angular/angular.js/blob/v1.6.0/CHANGELOG.md#breaking-changes
    $compileProvider.preAssignBindingsEnabled(true)
  })
  .run(($rootElement) => {
    if (!extractRawElm($rootElement).hasAttribute(GALLERY_INCLUDE_ATTR)) {
      throw new Error(deline(`
        Root#run(definition-check):
          root of gallery should be defined via [GALLERY_INCLUDE_ATTR=${GALLERY_INCLUDE_ATTR}] attr!
          check ${__dirname}/utils.bootstrap.js for more!
          ${getElmStr($rootElement)}`))
    }
  })
  .run((env, domh, addIdPrefix, $rootScope) => {
    if (env.requiresCssBodyReset) registerStyle(styles.bodyReset)
    if (env.requiresCssHardReset) registerStyle(styles.hardReset)

    function registerStyle(styleDDO) {
      const style = domh.createStyle(addIdPrefix(styleDDO.name))
      style.global(styleDDO.text)
      style.attach()
      $rootScope.$on('$destroy', style.detach)
    }
  })
  .run(($log, env) => {
    functions($log).forEach((fName) => {
      const func = $log[fName]
      const sign = `(${env.props.galleryKey})`
      const signSpace = `${sign} `
      $log[fName] = function() {
        if (typeof arguments[0] === 'string') {
          arguments[0] = signSpace + arguments[0]
          return func(...arguments)
        } else {
          return func(sign, ...arguments)
        }
      }
      $log[`_orig_${fName}`] = func
    })
  })
  .run(($log) => {
    $log.force = {}
    assign($log.force, $log)

    const order = ['debug', 'log', 'info', 'warn', 'error']
    const level = order.indexOf(envRaw.logLevel)

    for (let index = 0; index < level; index++) {
      const name = order[index]
      const func = $log[name]
      $log[name] = noop
      $log[`_dis_${name}`] = func
    }
  })
  .run((notification) => {
    const order = ['debug', 'info', 'warn', 'error']
    const level = order.indexOf(envRaw.notificationLevel)

    for (let index = 0; index < level; index++) {
      const name = order[index]
      const func = notification[name]
      notification[name] = noop
      notification[`_dis_${name}`] = func
    }
  })
  .run(($rootElement, $log, rootId, rootClass, env) => {
    const expDir = 'ltr'
    const actDir = $rootElement.attr('dir')
    if (expDir !== actDir) {
      $log.debug(deline(`
        Root#run($rootElement):
          expected $rootElement with [dir=${expDir}]
          but it has [dir=${actDir}] - fixed!`))
      $rootElement.prop('dir', expDir)
    }

    const actId = $rootElement.prop('id')
    if (rootId !== actId) {
      $log.debug(deline(`
        Root#run($rootElement):
          expected $rootElement with [id=${rootId}]
          but it has [id=${actId}] - fixed!`))
      $rootElement.prop('id', rootId)
    }

    if (!$rootElement.hasClass(rootClass)) {
      const actClass = $rootElement.prop('class')
      $log.debug(deline(`
        Root#run($rootElement):
          expected $rootElement with [class=${rootClass}]
          but it has [class=${actClass}] - fixed!`))
      $rootElement.addClass(rootClass)
    }

    const actKey = $rootElement.attr(GALLERY_INCLUDE_ATTR)
    if (env.props.galleryKey !== actKey) {
      $log.debug(deline(`
        Root#run($rootElement):
          expected $rootElement with [${GALLERY_INCLUDE_ATTR}=${env.props.galleryKey}]
          but it has [${GALLERY_INCLUDE_ATTR}=${actKey}] - fixed!`))
      $rootElement.attr(GALLERY_INCLUDE_ATTR, env.props.galleryKey)
    }
  })
  .run(($rootElement, $rootScope, domh) => {
    if (envRaw.useSelfKiller) {
      const stopHash = () => offEvent(window, 'hashchange', lazyCheck)
      const stopIntr = () => clearInterval(intrId)
      const destroyScope = () => $rootScope.$destroy()
      const destroyRootElm = () => domh.remove.notStrict($rootElement)

      const kill = once((isScopeDestroying = false) => {
        stopHash()
        stopIntr()
        if (!isScopeDestroying) destroyScope()
        destroyRootElm()
        console.info(`gallery is destroyed ${getElmStr($rootElement)}`)
      })

      const check = () => !domh.isRootInPage() && kill()
      const lazyCheck = () => setTimeout(check, 0)

      $rootScope.$on('$destroy', wrap(true, kill))
      const intrId = setInterval(check, 1e3)
      onEventPassive(window, 'hashchange', lazyCheck)
    }
  })
